import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Wand2, ListChecks, Send, ImageIcon, Repeat2, Clock, ArrowRight, Sparkles, Eye,
} from "lucide-react";
import OnboardingWizard from "@/components/OnboardingWizard";
import ReferralCard from "@/components/ReferralCard";
import PublishPreviewModal from "@/components/PublishPreviewModal";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="card-hover rounded-xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="text-3xl font-display font-bold">{value}</div>
  </div>
);

const STATUS_STYLE = {
  QUEUED: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  PUBLISHED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  FAILED: "bg-rose-500/20 text-rose-400 border-rose-500/40",
  EXPIRED_MAX_RECYCLES: "bg-muted text-muted-foreground border-border",
};

export default function Dashboard() {
  const { active } = useWorkspace();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [previewPost, setPreviewPost] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!active) return;
    api.get(`/posts?workspace_id=${active.id}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [active]);

  if (!active) return null;

  const queued = posts.filter((p) => p.status === "QUEUED").length;
  const published = posts.filter((p) => p.status === "PUBLISHED").length;
  const recycling = posts.filter((p) => p.recycle_enabled && p.status === "QUEUED").length;
  const plan = user?.plan;
  const monthlyImages = user?.usage?.images ?? 0;
  const cap = ["creator", "growth", "agency", "pro"].includes(plan) ? null : 5;
  const used = active.image_quota_used || 0;
  const upcoming = posts.filter((p) => p.status === "QUEUED")
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).slice(0, 5);

  return (
    <div className="space-y-8">
      <OnboardingWizard />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">{active.brand_name} · {active.niche}</p>
        </div>
        <Button data-testid="dashboard-create-post-button" onClick={() => navigate("/studio")} className="bg-primary hover:bg-primary/90 text-white">
          <Wand2 className="h-4 w-4 mr-2" /> Create post
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ListChecks} label="Queued" value={queued} accent="bg-amber-500/15 text-amber-400" testid="stat-queued" />
        <StatCard icon={Send} label="Published" value={published} accent="bg-emerald-500/15 text-emerald-400" testid="stat-published" />
        <StatCard icon={Repeat2} label="Recycling" value={recycling} accent="bg-primary/15 text-primary" testid="stat-recycling" />
        <StatCard icon={ImageIcon} label="Images used" value={cap ? `${used}/${cap}` : used} accent="bg-accent/15 text-accent" testid="stat-images" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Upcoming queue</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/calendar")} className="text-primary">
              View calendar <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          {upcoming.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Nothing queued yet. Generate your first post.
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3">
                  <Badge className={`border ${STATUS_STYLE[p.status]}`}>{p.status}</Badge>
                  <p className="text-sm truncate flex-1 min-w-0">{p.post_text}</p>
                  <Button
                    variant="ghost" size="sm"
                    data-testid={`preview-post-${p.id}`}
                    onClick={() => setPreviewPost(p)}
                    className="shrink-0 h-7 px-2 text-xs text-primary hover:bg-primary/10"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                  </Button>
                  <span className="text-xs font-mono text-muted-foreground shrink-0">
                    {new Date(p.scheduled_at).toLocaleDateString()} {new Date(p.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <div>
            <h3 className="font-semibold mb-1">Plan</h3>
            <Badge className="bg-primary/15 text-primary border-primary/30">
              {plan === "agency" ? "Agency — active"
                : plan === "growth" ? "E-commerce Growth — active"
                : (plan === "creator" || plan === "pro") ? (user?.is_legacy_pro ? "Creator (Legacy) — active" : "Creator — active")
                : "No plan — see plans"}
            </Badge>
          </div>
          {typeof user?.limits?.ai_posts === "number" && plan !== "none" && (
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">AI posts this month</span>
                <span className="font-mono">{user?.usage?.ai_posts ?? 0}/{user?.limits?.ai_posts ?? 15}</span>
              </div>
              <Progress value={((user?.usage?.ai_posts ?? 0) / (user?.limits?.ai_posts ?? 15)) * 100} className="h-2" data-testid="aiposts-usage-meter" />
            </div>
          )}
          {cap && (
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">{plan === "none" ? "Trial images" : "Image quota"}</span>
                <span className="font-mono">{used}/{cap}</span>
              </div>
              <Progress value={(used / cap) * 100} className="h-2" data-testid="quota-usage-meter" />
            </div>
          )}
          <Button data-testid="dashboard-upgrade-button" onClick={() => navigate("/billing")} variant="outline" className="w-full border-primary/40 text-primary hover:bg-primary/10">
            <Sparkles className="h-4 w-4 mr-2" /> {["creator", "growth", "agency", "pro"].includes(plan) ? "Manage plan" : "View plans"}
          </Button>
        </div>
      </div>

      {/* Referral rewards */}
      <ReferralCard />

      <PublishPreviewModal
        open={!!previewPost}
        onOpenChange={(o) => { if (!o) setPreviewPost(null); }}
        post={previewPost}
      />
    </div>
  );
}
